import React, {useState, useEffect, useCallback} from 'react'
import styled, {css} from 'styled-components'
import {
  ValidatedInputElement,
  Validation,
  ValidationMap
} from '../../../common/types'
import {Icon, IconName} from '../../../components/library/icon/icon'
import {Switch} from '../../../components/library/toggles/switch'
import {NodeValidators} from './nodes'
import {Button} from '../../../components/library/buttons'
import {Modal} from '../../../components/library/modal/modal'

const RulesInner = styled.div<{readOnly?: boolean}>`
  ${({readOnly, theme}) =>
    readOnly
      ? css`
          background: ${theme.white};
          border: solid 1px ${theme.grey20};
          border-top: none;
        `
      : css`
          background: ${theme.grey10};
        `}
  font-size: ${p => p.theme.fontSizeXS};
  line-height: ${p => p.theme.lineHeightXS};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 5px 5px 10px;
  border-radius: 0 0 ${({theme}) => theme.borderRadius}
    ${({theme}) => theme.borderRadius};
  > span.none {
    color: ${p => p.theme.grey50};
  }
  button {
    margin-left: 10px;
  }
`

export const ButtonGroup = styled.div`
  display: flex;

  /* allow submit button to be first in tab order */
  flex-direction: row-reverse;
  justify-content: flex-start;

  > * {
    margin-left: 10px;
  }
`

const ValidatorChildren = styled.div`
  max-height: 0px;
  overflow: hidden;
  pointer-events: none;
  &.open {
    max-height: none;
    pointer-events: auto;
    padding-bottom: 20px;
  }
  > :nth-child(n + 2) {
    margin-top: 1px;
  }
  > :first-of-type:not(:last-child) {
    border-radius: ${p => p.theme.borderRadius} ${p => p.theme.borderRadius} 0 0;
    > input {
      border-radius: ${p => p.theme.borderRadius} ${p => p.theme.borderRadius} 0
        0;
    }
  }
  > :last-child:not(:first-child) {
    border-radius: 0 0 ${p => p.theme.borderRadius} ${p => p.theme.borderRadius};
  }
`

const ValidatorHeader = styled.div``

const ValidatorItem = styled.div`
  border: 1px solid ${p => p.theme.grey10};
  border-bottom: 0;
  padding: 10px 20px 0px;
  ${ValidatorHeader} {
    padding: 5px 0 15px;
    display: flex;
    align-items: center;
    input {
      margin-left: auto;
    }
  }
`

const Heading = styled.div`
  font-size: ${p => p.theme.fontSize};
  line-height: ${p => p.theme.lineHeight};
  font-weight: bold;
  flex: 1;
`

const IconAligner = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  position: relative;
  color: ${p => p.theme.grey50};
`

export interface ValidationNodeProps<T extends Validation> {
  attributes: T['attributes']
  updateAttributes: (attributes: T['attributes']) => void
  enabled: boolean
  toggleEnabled: (enabled: boolean, attributes: T['attributes']) => void
  readOnly?: boolean
}

export interface ValidationRulesProps<T extends ValidatedInputElement> {
  node: T
  updateValidations: (validations: T['validations']) => void
  readOnly?: boolean
}

export const ValidationRules = ({
  node,
  updateValidations,
  readOnly
}: ValidationRulesProps<ValidatedInputElement>) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [localState, updateLocalState] = useState<ValidationMap | undefined>(
    node.validations
  )
  const [dirty, setDirty] = useState<boolean>(false)

  useEffect(() => {
    if (localState !== node.validations) {
      setDirty(true)
    }
  }, [localState, node.validations])

  useEffect(() => {
    updateLocalState(node.validations)
  }, [node.validations])

  const wrappedHandleClose = useCallback(() => {
    if (
      !dirty ||
      confirm(
        'You have unsaved changes - are you sure you want to discard them?'
      )
    ) {
      setModalOpen(false)
      setDirty(false)
      updateLocalState(node.validations) // revert changes
    }
  }, [setModalOpen, node.validations, dirty])

  const validatorsCount = node.validations
    ? Object.keys(node.validations).length
    : 0

  return (
    <RulesInner readOnly={readOnly}>
      {validatorsCount === 0 && <span className="none">No validation</span>}
      {validatorsCount === 1 && '1 validation rule'}
      {validatorsCount > 1 && `${validatorsCount} validation rules`}
      {readOnly && validatorsCount === 0 ? null : (
        <Button
          onClick={() => setModalOpen(!isModalOpen)}
          size="xsmall"
          appearance="transparent"
        >
          {validatorsCount > 0
            ? readOnly
              ? 'View'
              : 'Edit'
            : readOnly
            ? ''
            : 'Add'}
        </Button>
      )}
      <Modal
        title="Validation"
        isOpen={isModalOpen}
        handleClose={wrappedHandleClose}
      >
        <>
          <NodeValidators
            node={node}
            validations={localState}
            updateValidations={updateLocalState}
            readOnly={readOnly}
          />
          <ButtonGroup>
            <Button
              onClick={() => {
                updateValidations(localState)
                setDirty(false)
                setModalOpen(false)
              }}
              size="large"
              disabled={readOnly}
            >
              Save
            </Button>
            <Button onClick={wrappedHandleClose} appearance="tint" size="large">
              Cancel
            </Button>
          </ButtonGroup>
        </>
      </Modal>
    </RulesInner>
  )
}

interface ValidatorWrapperProps {
  icon: IconName
  title: string
  enabled: boolean
  children: React.ReactNode
  onEnableToggle: (enabled: boolean) => void
  readOnly?: boolean
}

export const ValidatorWrapper = ({
  icon,
  title,
  enabled,
  children,
  onEnableToggle,
  readOnly
}: ValidatorWrapperProps) => {
  return (
    <ValidatorItem className="validator-item">
      <ValidatorHeader>
        <IconAligner>
          <Icon name={icon} />
        </IconAligner>
        <Heading>{title}</Heading>
        <Switch
          checked={enabled}
          onChange={onEnableToggle}
          readOnly={readOnly}
        />
      </ValidatorHeader>
      <ValidatorChildren className={enabled ? 'open' : ''}>
        {children}
      </ValidatorChildren>
    </ValidatorItem>
  )
}
